import { useState, useMemo } from "react";
import { Reveal, Overline } from "@/components/motion";
import { OFFER } from "@/data/pricing";
import { Tag, ArrowRight } from "lucide-react";

const INR = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const Offers = ({ onBook }) => {
  const [amount, setAmount] = useState(2000);

  const active = useMemo(() => {
    // highest tier whose minimum is met
    let idx = -1;
    OFFER.tiers.forEach((t, i) => { if (amount >= t.min) idx = i; });
    return idx;
  }, [amount]);

  const pct = active >= 0 ? OFFER.tiers[active].pctNum : 0;
  const savings = (amount * pct) / 100;
  const payable = amount - savings;
  const toUnlock = OFFER.tiers[0].min - amount;

  return (
    <section id="offers" className="py-24 md:py-36 bg-secondary" data-testid="offers-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 lg:items-end">
          {/* Heading */}
          <div className="lg:col-span-6">
            <Reveal><Overline>Discounts & Offers</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]">
                {OFFER.headline}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-sm font-light text-foreground/78 leading-relaxed">{OFFER.note}</p>
            </Reveal>
          </div>

          {/* Interactive calculator */}
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="border border-foreground/25 p-7 md:p-9 bg-background" data-testid="savings-calculator">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-widest text-foreground/60">Estimate your saving</span>
                  <span className="font-serif text-3xl md:text-4xl" data-testid="calc-amount">{INR(amount)}</span>
                </div>

                <input
                  type="range"
                  min={0}
                  max={50000}
                  step={500}
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  data-testid="calc-slider"
                  className="mt-5 w-full cursor-pointer"
                  style={{ accentColor: "hsl(var(--foreground))" }}
                  aria-label="Order amount"
                />
                <div className="flex justify-between text-[11px] text-foreground/50 mt-1">
                  <span>₹0</span><span>₹50,000</span>
                </div>

                <div className="mt-6 pt-6 border-t border-foreground/15" data-testid="calc-result">
                  {pct === 0 ? (
                    <p className="text-base font-light text-foreground/80">
                      Spend <span className="font-medium text-foreground">{INR(toUnlock)}</span> more to unlock
                      <span className="font-medium text-foreground"> 25% off</span>.
                    </p>
                  ) : (
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-foreground/50">You save</div>
                        <div className="font-serif text-4xl md:text-5xl font-light" data-testid="calc-savings">{INR(savings)}</div>
                      </div>
                      <div className="text-foreground/40">
                        <ArrowRight size={22} />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-foreground/50">You pay ({pct}%)</div>
                        <div className="font-serif text-4xl md:text-5xl font-light" data-testid="calc-payable">{INR(payable)}</div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={onBook}
                  data-testid="calc-book-btn"
                  className="group relative mt-7 w-full bg-foreground text-background py-4 text-sm font-medium uppercase tracking-widest overflow-hidden"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2 transition-colors duration-300 group-hover:text-foreground">
                    Book &amp; Claim
                  </span>
                  <span className="absolute inset-0 bg-background translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Tier cards — active tier highlights based on the slider */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-foreground/15 border border-foreground/15">
          {OFFER.tiers.map((t, i) => {
            const isActive = i === active;
            return (
              <Reveal key={t.range} delay={i * 0.1} className={isActive ? "bg-foreground" : "bg-secondary"}>
                <button
                  onClick={() => setAmount(t.min)}
                  data-testid={`offer-tier-${i}`}
                  className={`w-full text-left p-8 lg:p-10 h-full flex flex-col justify-between min-h-[300px] transition-colors duration-300 ${
                    isActive ? "text-background" : "text-foreground hover:bg-foreground/[0.04]"
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-8">
                      <Tag size={14} className={isActive ? "text-background" : "text-foreground"} />
                      <span className={`text-xs uppercase tracking-widest ${isActive ? "text-background/60" : "text-foreground/50"}`}>{t.label}</span>
                    </div>
                    <div className="font-serif text-6xl md:text-7xl font-light leading-none">{t.pct}</div>
                    <p className={`mt-4 text-base font-light ${isActive ? "text-background/70" : "text-foreground/70"}`}>on orders</p>
                    <p className="font-sans text-lg font-medium">{t.range}</p>
                  </div>
                  <span className={`mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-widest border-t pt-5 w-full ${isActive ? "border-background/25 text-background" : "border-foreground/15 text-foreground/60"}`}>
                    {isActive ? "Selected — try it above" : "Tap to preview"} <ArrowRight size={13} />
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Offers;
